/**
 * The application root.
 *
 * Provider tree: QueryClient → RouterProvider.
 *
 * AuthProvider lives INSIDE a route layout so it can use `useNavigate()`
 * (React Router hooks require being inside <RouterProvider>).
 *
 * Every route is generated per locale from the manifest in `i18n/routes.ts`,
 * so `/pt-br/ordens-servico` and `/es-cl/ordenes-servicio` are two real routes
 * rather than one route with a variable segment. That is what makes
 * `/pt-br/ordenes-servicio` — the Spanish slug under the Portuguese prefix — a
 * 404 instead of a page that renders in the wrong language.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, redirect, RouterProvider, Outlet } from 'react-router-dom';

import { AuthProvider } from './auth/AuthContext.tsx';
import { detectLocale, setLocale, SUPPORTED_LOCALES } from './i18n/index.ts';
import {
  buildPath,
  homePath,
  LEGACY_PATHS,
  prefixFor,
  slugFor,
  type RouteId,
} from './i18n/routes.ts';
import { DashboardLayout } from './pages/DashboardLayout.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { NotificacoesList } from './pages/NotificacoesList.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { OrdemServicoDetail } from './pages/OrdemServicoDetail.tsx';
import { LaudoPage } from './pages/LaudoPage.tsx';
import { OrdensServicoList } from './pages/OrdensServicoList.tsx';
import { RotatePasswordPage } from './pages/RotatePasswordPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Wraps children in AuthProvider. Exists as a route element so AuthProvider
 * can use React Router hooks (useNavigate).
 */
function AuthWrapper(): React.JSX.Element {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

/**
 * One branch per locale.
 *
 * The branch loader switches the catalogue *before* the page renders. Doing it
 * in an effect instead would paint the previous language for a frame on every
 * cross-locale navigation.
 */
const localeBranches = SUPPORTED_LOCALES.map((locale) => ({
  path: prefixFor(locale),
  loader: async () => {
    await setLocale(locale);
    return null;
  },
  children: [
    { index: true, loader: () => redirect(homePath(locale)) },
    {
      path: slugFor('login', locale),
      element: <LoginPage />,
      handle: { routeId: 'login' satisfies RouteId },
    },
    {
      path: slugFor('rotate', locale),
      element: <RotatePasswordPage />,
      handle: { routeId: 'rotate' satisfies RouteId },
    },
    {
      element: <DashboardLayout />,
      children: [
        {
          path: slugFor('ordensServico', locale),
          element: <OrdensServicoList />,
          handle: { routeId: 'ordensServico' satisfies RouteId },
        },
        {
          path: `${slugFor('ordensServico', locale)}/:osId`,
          element: <OrdemServicoDetail />,
          handle: { routeId: 'ordemServicoDetalhe' satisfies RouteId },
        },
        {
          path: `${slugFor('ordensServico', locale)}/:osId/${slugFor('laudo', locale)}/:itemId/:tipo`,
          element: <LaudoPage />,
          handle: { routeId: 'laudo' satisfies RouteId },
        },
        {
          path: slugFor('notificacoes', locale),
          element: <NotificacoesList />,
          handle: { routeId: 'notificacoes' satisfies RouteId },
        },
      ],
    },
  ],
}));

/**
 * Unprefixed paths, kept as redirectors so old links and server-composed deep
 * links keep working. They resolve against the browser's language because at
 * this point we have nothing better — the customer has not told us anything.
 */
const legacyRedirects = LEGACY_PATHS.map(({ path, routeId }) => ({
  path,
  loader: ({ params }: { params: Record<string, string | undefined> }) =>
    redirect(
      buildPath(routeId, detectLocale(), {
        osId: params['osId'],
        itemId: params['itemId'],
        tipo: params['tipo'],
      }),
    ),
}));

const router = createBrowserRouter([
  {
    // AuthWrapper wraps all routes so AuthProvider can use React Router hooks.
    element: <AuthWrapper />,
    children: [
      ...localeBranches,
      ...legacyRedirects,
      { path: '/', loader: () => redirect(homePath(detectLocale())) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
